<template>
  <div>
    <li class="task-item" :data-ticket-id="localItem.id">
      <div class="task-item-header">
        <input
          v-if="selectionMode"
          type="checkbox"
          class="ticket-select-checkbox"
          :checked="isSelected"
          :disabled="isSelectionDisabled"
          @click.stop
          @change="handleToggleSelection"
        />
        <p class="task-title cursor-pointer" @click="handleShowEditModal">
          #{{ localItem.id }} {{ truncatedTitle(localItem.text, 40) }}
          <span v-if="companyName"> - {{ companyName }}</span>
        </p>
        <ticket-transfer-menu
          :ticket="localItem"
          @transfer-success="onTicketTransferred"
        />
      </div>
      <div class="task-item-body">
        <p class="money-card" v-if="isFunnelSale">
          {{ formatCurrency(localItem.sale_value) }}
        </p>

        <p v-if="metaPageName" class="meta-page-badge">
          {{ $t('TICKET.META_PAGE_LABEL') }}: {{ metaPageName }}
        </p>

        <span
          v-if="!showEditDescription"
          @click="handleShowEditDescription()"
          class="description cursor-pointer"
        >
          {{ truncatedTitle(descriptionWithoutMetaPage, 70) }}</span
        >

        <div v-if="showEditDescription">
          <textarea
            v-model="newDescription"
            class="form-control"
            rows="2"
            @blur="handleHideEditDescription"
          >
          </textarea>
          <woot-button
            size="tiny"
            color-scheme="primary"
            icon="save"
            @click="handleUpdateTexts"
          >
            {{ this.$t('TICKET.FORM.TEXT.SAVE') }}
          </woot-button>
        </div>
      </div>
      <div class="task-item-body card-select-default">
        <div
          class="task-priority"
          :class="[normalizedLocalPriority, { disabled: isAutomaticPriority }]"
          v-if="showTaskPriority"
          @click="changePriority"
        >
          {{ this.$t('TICKET.FORM.PRIORITY.LABEL_CARD') }}
          {{ priorityTranslate }}
        </div>
        <v-select
          v-if="showTaskPriorityDropdown"
          v-model="localItem.priority"
          ref="vueDropdown"
          :options="
            priorityOptions.map(option => ({
              label: option.name,
              value: option.value,
            }))
          "
          @input="setNewPriority"
          :clearable="false"
          :closeOnSelect="true"
          :reduce="option => option.value"
          class="custom-v-select"
        >
        </v-select>
      </div>
      <div>
        <div>
          <v-select
            v-if="showAgentDropdown"
            v-model="assignedAgent"
            :options="agentList"
            label="name"
            :get-option-label="option => option.name"
            @input="setNewAgent"
            :clearable="false"
            ref="agentDropdown"
            class="custom-v-select"
          />
          <woot-button
            v-if="showAgentDropdown"
            size="tiny"
            color-scheme="primary"
            icon="save"
            @click="changeAgent"
          >
            {{ this.$t('TICKET.FORM.TEXT.SAVE') }}
          </woot-button>
        </div>

        <div>
          <multiselect
            v-if="showLabelDropdown"
            v-model="selectedLabels"
            :options="labels"
            track-by="id"
            label="title"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
            placeholder="Pick some"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
            @input="setNewLabels"
          />
          <woot-button
            v-if="showLabelDropdown"
            size="tiny"
            color-scheme="primary"
            icon="save"
            @click="saveLabels"
          >
            {{ this.$t('TICKET.FORM.TEXT.SAVE') }}
          </woot-button>
        </div>
      </div>
      <div class="task-item-footer">
        <woot-button
          v-if="labelsSelected.length < 1"
          icon="add"
          variant="smooth"
          size="small"
          @click="toggleLabelDropdown"
        >
          {{ this.$t('TICKET.FORM.LABEL.ADD') }}
        </woot-button>
        <div class="flex">
          <div class="flex-direction-column">
            <div class="flex" @click="toggleLabelDropdown">
              <woot-label
                v-for="label in filteredLabels"
                :title="label.title"
                :color="label.color"
                :key="label.id"
                variant="smooth"
                small
                class="cursor-pointer"
              />
            </div>
            <woot-button
              v-if="validateLabels >= 22"
              :title="$t('CONVERSATION.CARD.SHOW_LABELS')"
              class="show-more--button"
              color-scheme="secondary"
              variant="hollow"
              icon="chevron-right"
              size="tiny"
              @click="handleShowEditModal"
            />
          </div>
        </div>
        <div class="box-thumbnail">
          <div class="cursor-pointer box-thumbnail">
            <div @click="changeAgent">
              <thumbnail
                v-if="assignedAgent"
                :src="assignedAgent.thumbnail"
                :username="assignedAgent.name"
                size="34px"
                v-tooltip.top-end="assignedAgent.name"
              />
            </div>
            <div @click="redirectContact" v-if="item.contact">
              <thumbnail
                :src="item.contact.avatar_url"
                :username="item.contact.name"
                size="34px"
                v-tooltip.top-end="item.contact.name"
              />
            </div>
          </div>
        </div>
      </div>
    </li>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Thumbnail from '../../../../../components/widgets/Thumbnail.vue';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import TicketTransferMenu from './TicketTransferMenu.vue';
import { mapGetters } from 'vuex';
import router from '../../../..';
import { frontendURL } from '../../../../../helper/URLHelper';
import { formatCurrency } from '../../../../../helper/formatCurrency';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  name: 'TaskItem',
  mixins: [alertMixin],
  props: [
    'item',
    'list',
    'board',
    'kanbanId',
    'isFunnelSale',
    'isSelected',
    'isSelectionDisabled',
    'selectionMode',
  ],
  components: {
    'v-select': vSelect,
    Thumbnail,
    SettingsSection,
    TicketTransferMenu,
  },
  data() {
    return {
      showTaskPriorityDropdown: false,
      showTaskPriority: true,
      newText: this.item.text,
      showEditDescription: false,
      newDescription: this.item.description,
      localItem: this.item,
      showAgentDropdown: false,
      showLabelDropdown: false,
      selectedLabels: this.labelsSelected,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      labels: 'labelsCrm/getLabels',
      accountId: 'getCurrentAccountId',
      kanbanList: 'getKanbanfunnels',
      companies: 'companies/getCompanies',
    }),

    companyName() {
      const companyId = this.localItem?.company_id;
      if (!companyId) return '';
      const company = (this.companies || []).find(c => c.id === companyId);
      return company?.name || '';
    },

    metaPageName() {
      const description = this.localItem?.description || '';
      const match = description.match(/^Página:\s*(.+?)(?:\n|$)/i);
      return match ? match[1].trim() : '';
    },

    descriptionWithoutMetaPage() {
      const description = this.localItem?.description || '';
      if (!this.metaPageName) return description;
      return description.replace(/^Página:\s*.+?(?:\n\n|\n|$)/i, '').trim();
    },

    normalizedLocalPriority() {
      const raw = (this.localItem?.priority || '').toString().toLowerCase();
      if (!raw) return '';
      if (raw.includes('critical')) return 'critical';
      if (raw.includes('high')) return 'high';
      if (raw.includes('medium')) return 'medium';
      if (raw.includes('low')) return 'low';
      return raw;
    },

    priorityTranslate() {
      const key = (this.normalizedLocalPriority || '').toUpperCase();
      const shortI18nKey = `CRM_DASHBOARD.PRIORITY_LABELS.${key}`;
      const shortTranslated = this.$t(shortI18nKey);
      if (shortTranslated && shortTranslated !== shortI18nKey)
        return shortTranslated;

      const i18nKey = `TICKET.FORM.PRIORITY.OPTIONS.${key}`;
      const translated = this.$t(i18nKey);
      if (translated && translated !== i18nKey) return translated;

      const p = this.normalizedLocalPriority;
      if (!p) return '';
      return p.charAt(0).toUpperCase() + p.slice(1);
    },

    priorityOptions() {
      return [
        {
          name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL'),
          value: 'critical',
        },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW'), value: 'low' },
        {
          name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM'),
          value: 'medium',
        },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH'), value: 'high' },
      ];
    },

    isAutomaticPriority() {
      const kanbanId = this.localItem?.kanbanfunnel_id;
      if (!kanbanId) return false;
      const kanban = (this.kanbanList || []).find(k => k.id === kanbanId);
      return (
        !!kanban?.enable_priority_questions &&
        Array.isArray(kanban?.priority_questions) &&
        kanban.priority_questions.length > 0
      );
    },

    labelsSelected() {
      if (!this.localItem?.labels_ids.length) return [];
      const labels = this.labels?.filter(label =>
        this.localItem?.labels_ids.includes(label.id)
      );
      this.selectedLabels = labels;
      return labels;
    },

    filteredLabels() {
      let totalCharacters = 0;

      return this.labelsSelected.filter(label => {
        const titleLength = label.title.length;

        if (titleLength >= 22) return false;

        if (totalCharacters + titleLength <= 22) {
          totalCharacters += titleLength;
          return true;
        }

        return false;
      });
    },

    validateLabels() {
      const selectedLabels = this.labelsSelected;
      const totalCharacters = selectedLabels.reduce((acc, label) => {
        return acc + (label.title ? label.title.length : 0);
      }, 0);
      return totalCharacters;
    },

    assignedAgent() {
      if (!this.agentList.length) {
        return null;
      }
      return this.agentList?.find(agent => agent.id === this.localItem.user_id);
    },
  },
  watch: {
    item: {
      handler(newItem) {
        this.localItem = { ...newItem };
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Abre a oportunidade automaticamente quando o CRM é aberto pela busca
    // global (?opportunity=<id>); limpa o parâmetro para não reabrir depois.
    const openId = this.$route.query.opportunity;
    if (openId && String(openId) === String(this.item.id)) {
      this.toggleUpdateTicketModal();
      const query = { ...this.$route.query };
      delete query.opportunity;
      this.$router.replace({ query }).catch(() => {});
    }
  },
  methods: {
    formatCurrency,

    handleToggleSelection(event) {
      this.$emit('toggle-selection', {
        ticket: this.localItem,
        selected: event.target.checked,
      });
    },

    toggleLabelDropdown() {
      this.showLabelDropdown = !this.showLabelDropdown;
    },

    async saveLabels() {
      const labelIds = this.selectedLabels.map(label => label.id);
      this.localItem.labels_ids = labelIds;
      this.item.labels_ids = labelIds;
      await this.$store.dispatch('updateTicket', this.localItem);
      this.toggleLabelDropdown();
    },

    handleHideEditDescription() {
      setTimeout(() => (this.showEditDescription = false), 200);
    },

    redirectContact() {
      const path = frontendURL(
        `accounts/${this.accountId}/contacts/${this.item.contact.id}`
      );
      router.push({ path });
    },

    async setNewLabels(newLabels) {
      this.selectedLabels = newLabels;
    },

    changePriority() {
      if (this.isAutomaticPriority) return;
      this.showTaskPriorityDropdown = !this.showTaskPriorityDropdown;
      this.showTaskPriority = !this.showTaskPriority;
      this.$nextTick(() => {
        const input = this.$refs.vueDropdown.$el.querySelector('input');
        input.focus();
      });
    },
    async setNewPriority(newPriority) {
      if (this.isAutomaticPriority) return;
      this.showTaskPriorityDropdown = !this.showTaskPriorityDropdown;
      this.showTaskPriority = !this.showTaskPriority;

      this.item.priority = newPriority;
      this.localItem.priority = newPriority;

      await this.$store.dispatch('updateTicket', this.localItem);
    },

    changeAgent() {
      this.showAgentDropdown = !this.showAgentDropdown;
      this.$nextTick(() => {
        if (this.showAgentDropdown) {
          const input = this.$refs.agentDropdown.$el.querySelector('input');
          input.focus();
        }
      });
    },

    async setNewAgent(agent) {
      this.localItem.user_id = agent.id;
      this.item.user_id = agent.id;
      await this.$store.dispatch('updateTicket', this.localItem);
      this.showAgentDropdown = false;
    },

    handleShowEditModal() {
      this.$router.push({
        name: 'crm_ticket',
        params: {
          accountId: this.accountId,
          ticketId: this.localItem.id,
        },
      });
    },
    handleShowEditDescription() {
      this.showEditDescription = !this.showEditDescription;
    },

    async handleUpdateTexts() {
      this.localItem.text = this.newText;
      this.localItem.description = this.newDescription;
      this.item.text = this.newText;
      this.item.description = this.newDescription;
      await this.$store.dispatch('updateTicket', this.localItem);
      this.showEditDescription = false;
    },

    truncatedTitle(text, maxCharacters = 30) {
      const value = text || '';
      return value.length > maxCharacters
        ? value.slice(0, maxCharacters) + '...'
        : value;
    },

    onTicketTransferred(updatedTicket) {
      if (!updatedTicket) return;

      const newKanbanId = Number(updatedTicket.kanbanfunnel_id);
      const currentKanbanId = Number(this.localItem.kanbanfunnel_id);

      if (newKanbanId !== currentKanbanId) {
        this.$emit('ticket-removed', this.localItem.id);
      } else {
        const safeUpdate = Object.fromEntries(
          Object.entries(updatedTicket).filter(
            ([, v]) => v !== null && v !== undefined
          )
        );
        this.localItem = { ...this.localItem, ...safeUpdate };
        Object.keys(safeUpdate).forEach(key => {
          this.$set(this.item, key, safeUpdate[key]);
        });
      }
    },
  },
};
</script>

<style>
.task-priority {
  display: inline;
  color: #fff;
  font-size: 13px;
  padding: 0.3rem 0.5rem;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  box-shadow: 0px 0px 5px -2px rgba(134, 134, 162, 1);
}

.task-priority.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.task-priority.low {
  background: linear-gradient(89deg, #66a7f8 0, #8ea7fb 100%);
}

.task-priority.medium {
  background: linear-gradient(89deg, #79d3b9 0, #81d5de 100%);
}

.task-priority.high {
  background: linear-gradient(89deg, #cd74d1 0, #db78a3 100%);
}

.task-priority.critical {
  background: linear-gradient(89deg, #ff5b5b 0, #ff8b5b 100%);
}

.ticket-select-checkbox {
  cursor: pointer;
  margin: 0;
}

.ticket-select-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.task-item {
  cursor: grab;
}

.money-card {
  color: #d0d0d0;
  font-weight: 500;
  font-size: 1.1rem;
}
.flex {
  display: flex;
  align-items: center;
  gap: 0.2rem;
}

.task-title:hover {
  text-decoration: underline;
}

.description {
  display: block;
  margin: var(--space-smaller) 0;
  color: var(--s-600);
  font-size: 1.4rem;
  line-height: 1.35;
  word-break: break-all;
}

.meta-page-badge {
  margin: 0 0 var(--space-smaller);
  padding: 2px 8px;
  display: inline-block;
  max-width: 100%;
  border-radius: var(--border-radius-small);
  background: var(--s-75);
  color: var(--s-700);
  font-size: var(--font-size-nano);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.assigned-users .user-avatar {
  margin-right: -15px;
}

.id-ticket {
  font-weight: bold;
}

.cursor-pointer {
  cursor: pointer;
}

.card-select-default {
  width: 50%;
}

.assigned-users .add-icon {
  margin-left: 20px;
  cursor: pointer;
}

.custom-v-select {
  font-size: 14px;
}

.assignee-selection .dropdown-item {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.4rem 0.8rem;
}

.assignee-selection .dropdown-item .user-avatar {
  margin-right: 15px;
}

.assignee-selection .dropdown-item .user-name {
  font-size: 14px;
  font-weight: 400;
  color: rgb(45, 45, 82);
}

.box-thumbnail {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
